// Refunds & disputes: per-gateway refund counts from local order tables.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

type Order = { id: string; user_id: string; status: string; created_at: string; amount?: number };

export default function AdminRefunds() {
  const [paddle, setPaddle] = useState<Order[]>([]);
  const [play, setPlay] = useState<Order[]>([]);
  const [raz, setRaz] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const since = new Date(Date.now() - 90 * 86400_000).toISOString();
      const [p, pl, r] = await Promise.all([
        supabase
          .from("paddle_orders")
          .select("id,user_id,status,created_at,amount_cents")
          .gte("created_at", since)
          .limit(5000),
        supabase
          .from("play_orders")
          .select("id,user_id,status,created_at,amount_micros")
          .gte("created_at", since)
          .limit(5000),
        supabase
          .from("razorpay_orders")
          .select("id,user_id,status,created_at,amount_paise")
          .gte("created_at", since)
          .limit(5000),
      ]);
      if (!active) return;
      setPaddle(
        (p.data ?? []).map((o) => ({
          id: o.id,
          user_id: o.user_id,
          status: o.status,
          created_at: o.created_at,
          amount: o.amount_cents / 100,
        })),
      );
      setPlay(
        (pl.data ?? []).map((o) => ({
          id: o.id,
          user_id: o.user_id,
          status: o.status,
          created_at: o.created_at,
          amount: Number(o.amount_micros ?? 0) / 1_000_000,
        })),
      );
      setRaz(
        (r.data ?? []).map((o) => ({
          id: o.id,
          user_id: o.user_id,
          status: o.status,
          created_at: o.created_at,
          amount: o.amount_paise / 100 / 83,
        })),
      );
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  const stats = useMemo(() => {
    const calc = (rows: Order[]) => {
      const paidRows = rows.filter((r) => r.status === "paid");
      const paid = paidRows.length;
      const paidUsd = paidRows.reduce((s, r) => s + (r.amount ?? 0), 0);
      const refundedRows = rows.filter((r) => r.status === "refunded");
      const refunded = refundedRows.length;
      const refundedUsd = refundedRows.reduce((s, r) => s + (r.amount ?? 0), 0);
      const rate = paid + refunded > 0 ? (refunded / (paid + refunded)) * 100 : 0;
      return { paid, paidUsd, refunded, refundedUsd, rate };
    };
    return { paddle: calc(paddle), play: calc(play), raz: calc(raz) };
  }, [paddle, play, raz]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const sections = [
    { name: "Paddle", s: stats.paddle, rows: paddle.filter((r) => r.status === "refunded") },
    { name: "Play Store", s: stats.play, rows: play.filter((r) => r.status === "refunded") },
    { name: "Razorpay", s: stats.raz, rows: raz.filter((r) => r.status === "refunded") },
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Refunds &amp; disputes</h1>
        <p className="text-sm text-muted-foreground">Last 90 days, per gateway.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {sections.map((sec) => (
          <Card key={sec.name}>
            <CardHeader>
              <CardTitle className="text-base">{sec.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Paid</div>
                  <div className="mt-0.5 text-lg font-semibold tabular-nums">{sec.s.paid}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Refunded</div>
                  <div className="mt-0.5 text-lg font-semibold tabular-nums text-destructive">{sec.s.refunded}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Rate</div>
                  <div className="mt-0.5 text-lg font-semibold tabular-nums">{sec.s.rate.toFixed(2)}%</div>
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground">
                Net refunded: ${sec.s.refundedUsd.toFixed(2)}
              </div>
              {sec.rows.length === 0 ? (
                <p className="rounded border border-dashed p-3 text-center text-xs text-muted-foreground">
                  No refunds in this period.
                </p>
              ) : (
                <div className="space-y-1">
                  {sec.rows.slice(0, 5).map((r) => (
                    <div key={r.id} className="rounded border p-2 text-[11px]">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[10px]">{r.user_id.slice(0, 14)}…</span>
                        <span className="tabular-nums">${(r.amount ?? 0).toFixed(2)}</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
